import { useEffect, useRef, useState } from 'react'
import { IconoBuscar } from '../../componentes/iconos'
import {
  ESTADOS,
  NIVELES_FILTRO,
  type FiltrosAvisos,
  type NivelAviso,
} from '../../hooks/useFiltrosAvisos'
import type { ResumenAvisos } from '../../hooks/useAvisos'
import { etiquetaEstadoPlural } from './formato'
import { SEMANAS_ATENCION, SEMANAS_CRITICO } from './semanas'

/** Sin espera, cada tecla seria una peticion y una entrada de historial. */
const ESPERA_BUSQUEDA = 300

/**
 * Los dos primeros son los mismos umbrales con los que se destacan las filas:
 * el operador filtra por lo que la tabla ya le esta senalando, en vez de tener
 * que traducir entre dos escalas distintas.
 */
const CORTES_SEMANAS = [SEMANAS_ATENCION, SEMANAS_CRITICO, 12] as const

/** Etiqueta + color de punto de cada nivel para la botonera. */
const NIVEL_META: Record<NivelAviso, { etiqueta: string; dot: string }> = {
  bien: { etiqueta: 'Bien', dot: 'bg-positivo-anillo' },
  atencion: { etiqueta: 'Atencion', dot: 'bg-atencion-anillo' },
  critico: { etiqueta: 'Critico', dot: 'bg-critico-anillo' },
}

interface Props {
  filtros: FiltrosAvisos
  onCambiar: (clave: 'estado' | 'semanas_minimas' | 'q' | 'nivel' | 'orden', valor: string | undefined) => void
  resumen?: ResumenAvisos
}

const CLASE_CAMPO =
  'w-full rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm text-tinta outline-none focus:border-foco focus:ring-2 focus:ring-foco-halo'

/** Cuántos hay de cada nivel, del resumen (undefined mientras carga). */
function conteoNivel(resumen: ResumenAvisos | undefined, nivel: NivelAviso | 'todos'): number | undefined {
  if (!resumen) return undefined
  if (nivel === 'todos') return resumen.total
  if (nivel === 'bien') return resumen.nivel_bien
  if (nivel === 'atencion') return resumen.nivel_atencion
  return resumen.nivel_critico
}

export function BarraFiltrosAvisos({ filtros, onCambiar, resumen }: Props) {
  const [texto, setTexto] = useState(filtros.q)
  // Ultimo valor que este componente ha puesto en la url. Sirve para distinguir
  // "lo he escrito yo" de "la url ha cambiado por fuera" (atras del navegador).
  const ultimoEnviado = useRef(filtros.q)

  useEffect(() => {
    if (filtros.q === ultimoEnviado.current) return
    ultimoEnviado.current = filtros.q
    setTexto(filtros.q)
  }, [filtros.q])

  useEffect(() => {
    if (texto === ultimoEnviado.current) return
    const temporizador = setTimeout(() => {
      ultimoEnviado.current = texto
      onCambiar('q', texto || undefined)
    }, ESPERA_BUSQUEDA)
    return () => clearTimeout(temporizador)
  }, [texto, onCambiar])

  const sufijo = (n: number | undefined) => (n === undefined ? '' : ` · ${n}`)

  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]">
        <div>
          <label htmlFor="buscador-avisos" className="block text-sm font-medium text-tinta-2">
            Buscar cliente
          </label>
          <div className="relative mt-1">
            <IconoBuscar
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-tinta-apagada"
            />
            <input
              id="buscador-avisos"
              type="search"
              autoComplete="off"
              placeholder="Nombre o telefono"
              value={texto}
              onChange={(evento) => setTexto(evento.target.value)}
              className={`${CLASE_CAMPO} pl-9`}
            />
          </div>
        </div>

        <div>
          <label htmlFor="orden-aviso" className="block text-sm font-medium text-tinta-2">
            Orden
          </label>
          <select
            id="orden-aviso"
            value={filtros.orden ?? 'normales'}
            onChange={(evento) => onCambiar('orden', evento.target.value === 'criticos' ? 'criticos' : undefined)}
            className={`${CLASE_CAMPO} mt-1`}
          >
            <option value="normales">Los que estan bien primero</option>
            <option value="criticos">Criticos primero</option>
          </select>
        </div>

        <div>
          <label htmlFor="estado-aviso" className="block text-sm font-medium text-tinta-2">
            Estado
          </label>
          <select
            id="estado-aviso"
            value={filtros.estado ?? ''}
            onChange={(evento) => onCambiar('estado', evento.target.value || undefined)}
            className={`${CLASE_CAMPO} mt-1`}
          >
            {/* Las opciones salen del enum del documento OpenAPI: una lista a mano
                acabaria ofreciendo un valor que el endpoint rechaza con un 422. */}
            <option value="">Todos los estados</option>
            {ESTADOS.map((estado) => (
              <option key={estado} value={estado}>
                {etiquetaEstadoPlural(estado)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Botonera de nivel (opción C): cada botón muestra cuántos hay. El nivel
          es un filtro aparte de "semanas sin pedir" (el desplegable de abajo). */}
      <div>
        <span className="block text-sm font-medium text-tinta-2">Nivel</span>
        <div
          role="group"
          aria-label="Filtrar por nivel"
          className="mt-1 inline-flex overflow-hidden rounded-lg border border-borde-fuerte"
        >
          <button
            type="button"
            aria-pressed={filtros.nivel === undefined}
            onClick={() => onCambiar('nivel', undefined)}
            className={[
              'px-3.5 py-1.5 text-sm font-semibold transition-colors',
              filtros.nivel === undefined ? 'bg-acento text-tinta-inversa' : 'text-tinta-2 hover:bg-elevado',
            ].join(' ')}
          >
            Todos{sufijo(conteoNivel(resumen, 'todos'))}
          </button>
          {NIVELES_FILTRO.map((nivel) => {
            const activo = filtros.nivel === nivel
            return (
              <button
                key={nivel}
                type="button"
                aria-pressed={activo}
                onClick={() => onCambiar('nivel', activo ? undefined : nivel)}
                className={[
                  'flex items-center gap-2 border-l border-borde-fuerte px-3.5 py-1.5 text-sm font-semibold transition-colors',
                  activo ? 'bg-acento text-tinta-inversa' : 'text-tinta-2 hover:bg-elevado',
                ].join(' ')}
              >
                <span className={`size-2 rounded-full ${NIVEL_META[nivel].dot}`} aria-hidden="true" />
                {NIVEL_META[nivel].etiqueta}
                {sufijo(conteoNivel(resumen, nivel))}
              </button>
            )
          })}
        </div>
      </div>

      <div className="sm:max-w-xs">
        <label htmlFor="semanas-minimas" className="block text-sm font-medium text-tinta-2">
          Semanas sin pedir
        </label>
        <select
          id="semanas-minimas"
          value={filtros.semanas_minimas === undefined ? '' : String(filtros.semanas_minimas)}
          onChange={(evento) => onCambiar('semanas_minimas', evento.target.value || undefined)}
          className={`${CLASE_CAMPO} mt-1`}
        >
          <option value="">Todas</option>
          {CORTES_SEMANAS.map((corte) => (
            <option key={corte} value={corte}>
              {corte} o mas
            </option>
          ))}
        </select>
      </div>
    </div>
  )
}
